/**
 * Authorize.Net Recurring Billing (ARB) & Customer Information Manager (CIM)
 * Supports automated monthly payments and stored payment profiles
 */

export class AuthorizeNetRecurring {
  constructor() {
    this.apiLoginId = import.meta.env.VITE_AUTHORIZE_NET_API_LOGIN_ID;
    this.transactionKey = import.meta.env.VITE_AUTHORIZE_NET_TRANSACTION_KEY;
    this.sandboxMode = false; // PRODUCTION MODE
    
    this.endpoint = this.sandboxMode 
      ? 'https://apitest.authorize.net/xml/v1/request.api'
      : 'https://api.authorize.net/xml/v1/request.api';
  }

  /**
   * Make API request to Authorize.Net
   */
  async makeRequest(payload) {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const text = await response.text();
      // Remove BOM if present
      const cleanText = text.replace(/^\uFEFF/, '');
      return JSON.parse(cleanText);
    } catch (error) {
      console.error('Authorize.Net API Error:', error);
      throw error;
    }
  }

  // ==========================================
  // CUSTOMER INFORMATION MANAGER (CIM)
  // Store customer payment profiles securely
  // ==========================================

  /**
   * Create a customer profile (stores customer info for future charges)
   */
  async createCustomerProfile({ customerId, email, description, paymentProfile }) {
    // Build profile object - only include paymentProfiles if we have card data
    const profile = {
      merchantCustomerId: customerId,
      description: description || `Customer ${customerId}`,
      email: email
    };

    // Only add paymentProfiles if card data is provided
    if (paymentProfile && paymentProfile.cardNumber) {
      profile.paymentProfiles = [{
        customerType: 'individual',
        billTo: {
          firstName: paymentProfile.firstName,
          lastName: paymentProfile.lastName,
          address: paymentProfile.address,
          city: paymentProfile.city,
          state: paymentProfile.state,
          zip: paymentProfile.zip,
          country: 'USA',
          phoneNumber: paymentProfile.phone
        },
        payment: {
          creditCard: {
            cardNumber: paymentProfile.cardNumber.replace(/\s/g, ''),
            expirationDate: paymentProfile.expirationDate,
            cardCode: paymentProfile.cvv
          }
        }
      }];
    }

    const payload = {
      createCustomerProfileRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        profile: profile,
        validationMode: this.sandboxMode ? 'testMode' : 'liveMode'
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        customerProfileId: response.customerProfileId,
        paymentProfileIds: response.customerPaymentProfileIdList || [],
        message: 'Customer profile created successfully'
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to create customer profile',
        errorCode: response.messages?.message?.[0]?.code
      };
    }
  }

  /**
   * Get customer profile details
   */
  async getCustomerProfile(customerProfileId) {
    const payload = {
      getCustomerProfileRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        customerProfileId: customerProfileId,
        includeIssuerInfo: true
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        profile: response.profile
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to get customer profile'
      };
    }
  }

  /**
   * Add a payment profile to existing customer
   */
  async addPaymentProfile(customerProfileId, paymentData) {
    const payload = {
      createCustomerPaymentProfileRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        customerProfileId: customerProfileId,
        paymentProfile: {
          customerType: 'individual',
          billTo: {
            firstName: paymentData.firstName,
            lastName: paymentData.lastName,
            address: paymentData.address,
            city: paymentData.city,
            state: paymentData.state,
            zip: paymentData.zip,
            country: 'USA',
            phoneNumber: paymentData.phone
          },
          payment: {
            creditCard: {
              cardNumber: paymentData.cardNumber.replace(/\s/g, ''),
              expirationDate: paymentData.expirationDate,
              cardCode: paymentData.cvv
            }
          },
          defaultPaymentProfile: paymentData.isDefault || false
        },
        validationMode: this.sandboxMode ? 'testMode' : 'liveMode'
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        paymentProfileId: response.customerPaymentProfileId,
        message: 'Payment method added successfully'
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to add payment method'
      };
    }
  }

  /**
   * Update a payment profile
   */
  async updatePaymentProfile(customerProfileId, paymentProfileId, paymentData) {
    const payload = {
      updateCustomerPaymentProfileRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        customerProfileId: customerProfileId,
        paymentProfile: {
          customerPaymentProfileId: paymentProfileId,
          billTo: {
            firstName: paymentData.firstName,
            lastName: paymentData.lastName,
            address: paymentData.address,
            city: paymentData.city,
            state: paymentData.state,
            zip: paymentData.zip,
            country: 'USA'
          },
          payment: {
            creditCard: {
              cardNumber: paymentData.cardNumber.replace(/\s/g, ''),
              expirationDate: paymentData.expirationDate,
              cardCode: paymentData.cvv
            }
          },
          defaultPaymentProfile: paymentData.isDefault || false
        },
        validationMode: this.sandboxMode ? 'testMode' : 'liveMode'
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        message: 'Payment method updated successfully'
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to update payment method'
      };
    }
  }

  /**
   * Delete a payment profile
   */
  async deletePaymentProfile(customerProfileId, paymentProfileId) {
    const payload = {
      deleteCustomerPaymentProfileRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        customerProfileId: customerProfileId,
        customerPaymentProfileId: paymentProfileId
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        message: 'Payment method deleted successfully'
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to delete payment method'
      };
    }
  }

  /**
   * Charge a stored payment profile (one-time charge)
   */
  async chargeCustomerProfile(customerProfileId, paymentProfileId, amount, description) {
    const payload = {
      createTransactionRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        transactionRequest: {
          transactionType: 'authCaptureTransaction',
          amount: amount.toFixed(2),
          profile: {
            customerProfileId: customerProfileId,
            paymentProfile: {
              paymentProfileId: paymentProfileId
            }
          },
          order: {
            description: description || 'Monthly payment'
          }
        }
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.transactionResponse?.responseCode === '1') {
      return {
        success: true,
        transactionId: response.transactionResponse.transId,
        authCode: response.transactionResponse.authCode,
        message: 'Payment successful'
      };
    } else {
      return {
        success: false,
        error: response.transactionResponse?.errors?.[0]?.errorText || 'Payment failed',
        errorCode: response.transactionResponse?.errors?.[0]?.errorCode
      };
    }
  }

  // ==========================================
  // AUTOMATED RECURRING BILLING (ARB)
  // Create and manage subscriptions
  // ==========================================

  /**
   * Create a recurring subscription
   */
  async createSubscription({
    name,
    amount,
    startDate,
    intervalLength = 1,
    intervalUnit = 'months',
    totalOccurrences = 9999, // 9999 = unlimited
    trialAmount = null,
    trialOccurrences = 0,
    customer,
    paymentProfile
  }) {
    // Format start date as YYYY-MM-DD
    const formattedStartDate = startDate instanceof Date 
      ? startDate.toISOString().split('T')[0]
      : startDate;

    const payload = {
      ARBCreateSubscriptionRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        subscription: {
          name: name,
          paymentSchedule: {
            interval: {
              length: intervalLength,
              unit: intervalUnit
            },
            startDate: formattedStartDate,
            totalOccurrences: totalOccurrences,
            trialOccurrences: trialOccurrences
          },
          amount: amount.toFixed(2),
          trialAmount: trialAmount ? trialAmount.toFixed(2) : '0.00',
          order: {
            invoiceNumber: `INV-${Date.now()}`,
            description: name
          },
          payment: {
            creditCard: {
              cardNumber: paymentProfile.cardNumber.replace(/\s/g, ''),
              expirationDate: paymentProfile.expirationDate,
              cardCode: paymentProfile.cvv
            }
          },
          customer: {
            id: customer.id || `CUST-${Date.now()}`,
            email: customer.email,
            phoneNumber: customer.phone
          },
          billTo: {
            firstName: customer.firstName,
            lastName: customer.lastName,
            address: customer.address,
            city: customer.city,
            state: customer.state,
            zip: customer.zip,
            country: 'USA'
          }
        }
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        subscriptionId: response.subscriptionId,
        message: 'Subscription created successfully'
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to create subscription',
        errorCode: response.messages?.message?.[0]?.code
      };
    }
  }

  /**
   * Create subscription using stored payment profile
   */
  async createSubscriptionFromProfile({
    name,
    amount,
    startDate,
    intervalLength = 1,
    intervalUnit = 'months',
    totalOccurrences = 9999,
    customerProfileId,
    paymentProfileId,
    customer
  }) {
    const formattedStartDate = startDate instanceof Date 
      ? startDate.toISOString().split('T')[0]
      : startDate;

    const payload = {
      ARBCreateSubscriptionRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        subscription: {
          name: name,
          paymentSchedule: {
            interval: {
              length: intervalLength,
              unit: intervalUnit
            },
            startDate: formattedStartDate,
            totalOccurrences: totalOccurrences,
            trialOccurrences: 0
          },
          amount: amount.toFixed(2),
          trialAmount: '0.00',
          order: {
            invoiceNumber: `INV-${Date.now()}`,
            description: name
          },
          profile: {
            customerProfileId: customerProfileId,
            customerPaymentProfileId: paymentProfileId
          }
        }
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        subscriptionId: response.subscriptionId,
        message: 'Subscription created successfully'
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to create subscription',
        errorCode: response.messages?.message?.[0]?.code
      };
    }
  }

  /**
   * Get subscription details
   */
  async getSubscription(subscriptionId) {
    const payload = {
      ARBGetSubscriptionRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        subscriptionId: subscriptionId,
        includeTransactions: true
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        subscription: response.subscription,
        arbTransactions: response.arbTransactions || []
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to get subscription'
      };
    }
  }

  /**
   * Get subscription status
   */
  async getSubscriptionStatus(subscriptionId) {
    const payload = {
      ARBGetSubscriptionStatusRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        subscriptionId: subscriptionId
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        status: response.status, // active, expired, suspended, canceled, terminated
        message: `Subscription is ${response.status}`
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to get subscription status'
      };
    }
  }

  /**
   * Update a subscription (amount, payment info, etc.)
   */
  async updateSubscription(subscriptionId, updates) {
    const subscriptionUpdate = {};
    
    if (updates.name) subscriptionUpdate.name = updates.name;
    if (updates.amount) subscriptionUpdate.amount = updates.amount.toFixed(2);
    
    if (updates.paymentProfile) {
      subscriptionUpdate.payment = {
        creditCard: {
          cardNumber: updates.paymentProfile.cardNumber.replace(/\s/g, ''),
          expirationDate: updates.paymentProfile.expirationDate,
          cardCode: updates.paymentProfile.cvv
        }
      };
    }

    if (updates.customer) {
      subscriptionUpdate.billTo = {
        firstName: updates.customer.firstName,
        lastName: updates.customer.lastName,
        address: updates.customer.address,
        city: updates.customer.city,
        state: updates.customer.state,
        zip: updates.customer.zip,
        country: 'USA'
      };
    }

    const payload = {
      ARBUpdateSubscriptionRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        subscriptionId: subscriptionId,
        subscription: subscriptionUpdate
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        message: 'Subscription updated successfully'
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to update subscription'
      };
    }
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(subscriptionId) {
    const payload = {
      ARBCancelSubscriptionRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        subscriptionId: subscriptionId
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        message: 'Subscription canceled successfully'
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to cancel subscription'
      };
    }
  }

  /**
   * Get list of subscriptions (with optional filters)
   */
  async getSubscriptionList(searchType = 'subscriptionActive', sorting = { orderBy: 'submitTimeUtc', orderDescending: true }, paging = { limit: 100, offset: 1 }) {
    const payload = {
      ARBGetSubscriptionListRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        searchType: searchType, // subscriptionActive, subscriptionInactive, subscriptionExpiringThisMonth, cardExpiringThisMonth
        sorting: sorting,
        paging: paging
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        subscriptions: response.subscriptionDetails || [],
        totalNumInResultSet: response.totalNumInResultSet || 0
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to get subscription list'
      };
    }
  }

  // ==========================================
  // TRANSACTION HISTORY
  // ==========================================

  /**
   * Get settled batch list (transaction history)
   */
  async getSettledBatchList(startDate, endDate) {
    const payload = {
      getSettledBatchListRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        firstSettlementDate: startDate,
        lastSettlementDate: endDate
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        batches: response.batchList || []
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to get batch list'
      };
    }
  }

  /**
   * Get transaction details
   */
  async getTransactionDetails(transactionId) {
    const payload = {
      getTransactionDetailsRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        transId: transactionId
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        transaction: response.transaction
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to get transaction details'
      };
    }
  }

  /**
   * Get unsettled transaction list
   */
  async getUnsettledTransactionList() {
    const payload = {
      getUnsettledTransactionListRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        }
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.messages?.resultCode === 'Ok') {
      return {
        success: true,
        transactions: response.transactions || []
      };
    } else {
      return {
        success: false,
        error: response.messages?.message?.[0]?.text || 'Failed to get unsettled transactions'
      };
    }
  }

  /**
   * Refund a transaction
   */
  async refundTransaction(transactionId, amount, cardNumber) {
    const payload = {
      createTransactionRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        transactionRequest: {
          transactionType: 'refundTransaction',
          amount: amount.toFixed(2),
          payment: {
            creditCard: {
              cardNumber: cardNumber.slice(-4), // Last 4 digits only for refunds
              expirationDate: 'XXXX'
            }
          },
          refTransId: transactionId
        }
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.transactionResponse?.responseCode === '1') {
      return {
        success: true,
        transactionId: response.transactionResponse.transId,
        message: 'Refund processed successfully'
      };
    } else {
      return {
        success: false,
        error: response.transactionResponse?.errors?.[0]?.errorText || 'Refund failed'
      };
    }
  }

  /**
   * Void a transaction (before settlement)
   */
  async voidTransaction(transactionId) {
    const payload = {
      createTransactionRequest: {
        merchantAuthentication: {
          name: this.apiLoginId,
          transactionKey: this.transactionKey
        },
        transactionRequest: {
          transactionType: 'voidTransaction',
          refTransId: transactionId
        }
      }
    };

    const response = await this.makeRequest(payload);
    
    if (response.transactionResponse?.responseCode === '1') {
      return {
        success: true,
        transactionId: response.transactionResponse.transId,
        message: 'Transaction voided successfully'
      };
    } else {
      return {
        success: false,
        error: response.transactionResponse?.errors?.[0]?.errorText || 'Void failed'
      };
    }
  }
}

const authorizeNetRecurring = new AuthorizeNetRecurring();
export { authorizeNetRecurring };
export default authorizeNetRecurring;
